import React from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Stack, Typography } from '@mui/material';
import { di } from '../../../../state/inversify.config';
import { dateTime } from '../../../../time/datetime';
import { AccountTypes } from '../../../../components/changeUser/accountTypes.enum';
import { AuthService } from '../../../../state/helperServices/auth/auth.service';
import { Button } from '../../../../components/button/button';
import { Assessment } from '../../../../state/reduxRepositories/assessments/assessments.interfaces';

interface SummaryCardProps {
  assessment: Assessment;
}
export function SummaryCard({ assessment }: SummaryCardProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const authService = di.get<AuthService>(AuthService);
  const loggedInUser = authService.useLoggedInUserSelector();

  const assessmentFor = (): string => {
    return t('assessment.ASSESSMENT_SUMMARY_PATIENT_AND_DOCTOR_DETAILS', {
      patientName: assessment.playerFullName,
      assessmentDateTime: dateTime.format(
        assessment.createdAt,
        t('common.datetime.FORMAT_ABBREVIATED_DATE_TIME'),
      ),
    });
  };

  const result = () => {
    if (!(assessment.assessment.clearedToPlay === undefined)) {
      if (assessment.assessment.clearedToPlay) {
        return t('assessment.PATIENT_CLEAR_TO_PLAY');
      }
      return t('assessment.PATIENT_NOT_CLEAR_TO_PLAY');
    }

    return t('assessment.ID_GENERIC_NOT_APPLICABLE');
  };

  const onPressNavigateToNormatives = () => {
    navigate(`/clinic/assessment/normatives`);
  };

  return (
    <Stack direction="column" spacing={3}>
      <Stack direction="row">
        <Typography variant="h6" sx={{ my: 2 }}>
          {assessmentFor()}
        </Typography>
      </Stack>
      <Stack direction="row">
        <Trans
          i18nKey="assessment.ASSESSMENT_SUMMARY_PATIENT_DETAILS"
          values={{ patientName: assessment.playerFullName }}
        />
      </Stack>
      <Stack direction="row">
        <Trans
          i18nKey="assessment.ASSESSMENT_SUMMARY_ASSESSOR_DETAILS"
          values={{ assessorName: assessment.createdByUserFullName }}
        />
      </Stack>
      <Stack direction="row">
        <Trans
          i18nKey="assessment.ASSESSMENT_SUMMARY_DATE"
          values={{
            assessmentDateTime: dateTime.format(
              assessment.createdAt,
              t('common.datetime.FORMAT_ABBREVIATED_DATE_TIME'),
            ),
          }}
        />
      </Stack>
      <Stack direction="row">
        <Trans
          i18nKey="assessment.ASSESSMENT_SUMMARY_CLEARANCE_RESULT"
          values={{
            clearance: result(),
          }}
        />
      </Stack>
      <Stack direction="row">
        {loggedInUser?.accountType === AccountTypes.Clinic && (
          <Stack direction="row" spacing={2} alignItems="center">
            <Typography>
              {t('assessment.ASSESSMENT_SUMMARY_MORE_INFO') || ''}
            </Typography>

            <Button
              color="secondary"
              label={t('assessment.ASSESSMENT_NORMATIVES_TITLE') || ''}
              variant="contained"
              onClick={onPressNavigateToNormatives}
              size="small"
              type="button"
            />
          </Stack>
        )}
      </Stack>
    </Stack>
  );
}
